import { lazy, Suspense, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  Database,
  Download,
  ExternalLink,
  FileText,
  FileUp,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { dimensionLabels, questions } from "./data/questions";
import { downloadSampleCsv, sampleCostOptimizationCsv, sampleCsvFileName } from "./data/sampleCsv";
import { analyzeCostOptimizationCsv } from "./engine/costCsv";
import { enhanceAssessmentNarrative } from "./engine/aiNarrative";
import { buildAssessmentResult, calculateDimensions } from "./engine/strategy";
import {
  dimensionOrder,
  emptyContext,
  type AnswerMap,
  type AssessmentResult,
  type CompanyContext,
  type CostImportAnalysis,
} from "./engine/types";

const PdfExampleViewer = lazy(() => import("./PdfExampleViewer"));

type Screen = "intro" | "import" | "context" | "questions" | "review" | "result";

interface AppHistoryState {
  __finopsAssessment: true;
  screen: Screen;
  context: CompanyContext;
  answers: AnswerMap;
  costAnalysis: CostImportAnalysis | null;
  questionIndex: number;
  result: AssessmentResult | null;
  depth: number;
}

const baseState = (): AppHistoryState => ({
  __finopsAssessment: true,
  screen: "intro",
  context: emptyContext,
  answers: {},
  costAnalysis: null,
  questionIndex: 0,
  result: null,
  depth: 0,
});

const isAppHistoryState = (value: unknown): value is AppHistoryState =>
  Boolean(value && typeof value === "object" && (value as AppHistoryState).__finopsAssessment === true);

const routeFor = (state: AppHistoryState) => {
  const params = new URLSearchParams();
  if (state.screen !== "intro") params.set("step", state.screen === "questions" ? "question" : state.screen);
  if (state.screen === "questions") params.set("n", String(state.questionIndex + 1));
  const query = params.toString();
  return `${window.location.pathname}${query ? `?${query}` : ""}`;
};

const initialAppState = (): AppHistoryState => {
  if (isAppHistoryState(window.history.state)) return window.history.state;
  const params = new URLSearchParams(window.location.search);
  if (params.get("step") === "context") return { ...baseState(), screen: "context" };
  if (params.get("step") === "import") return { ...baseState(), screen: "import" };
  return baseState();
};

const contextLabels: Record<keyof CompanyContext, string> = {
  companyName: "Company name",
  respondentName: "Your name",
  role: "Your role",
  industry: "Industry",
  annualAwsSpend: "Approximate annual AWS spend",
  awsAccounts: "AWS account footprint",
  primaryGoal: "Primary FinOps goal",
  planningHorizon: "Planning horizon",
  estimatedMonthlyOpportunity: "AWS-estimated monthly opportunity",
  recommendationVolume: "Approximate recommendation volume",
};

const selectOptions: Partial<Record<keyof CompanyContext, Array<[string, string]>>> = {
  annualAwsSpend: [["under100k", "Under $100K"], ["100k-500k", "$100K-$500K"], ["500k-2m", "$500K-$2M"], ["2m-10m", "$2M-$10M"], ["10m+", "$10M+"]],
  awsAccounts: [["1-3", "1-3 accounts"], ["4-10", "4-10 accounts"], ["11-50", "11-50 accounts"], ["51+", "51+ accounts"]],
  primaryGoal: [["reduce", "Realize AWS optimization value"], ["forecast", "Make commitment decisions safer"], ["unit-economics", "Connect cost to business value"], ["governance", "Build accountable execution"], ["modernize", "Fund architecture modernization"]],
  planningHorizon: [["immediate", "Immediate intervention"], ["quarter", "Next 90 days"], ["year", "Next 12 months"]],
  estimatedMonthlyOpportunity: [["unknown", "Not known"], ["under-10k", "Under $10K per month"], ["10k-50k", "$10K-$50K per month"], ["50k-150k", "$50K-$150K per month"], ["150k-plus", "$150K+ per month"]],
  recommendationVolume: [["unknown", "Not known"], ["under-100", "Under 100"], ["100-500", "100-500"], ["500-2000", "500-2,000"], ["2000-plus", "More than 2,000"]],
};

const fieldOrder: Array<keyof CompanyContext> = ["companyName", "respondentName", "role", "industry", "annualAwsSpend", "primaryGoal", "planningHorizon"];

const money = (value: number, compact = false) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 0, notation: compact ? "compact" : "standard",
}).format(value);

function StrategySignal({ answers, compact = false }: { answers: AnswerMap; compact?: boolean }) {
  const dimensions = calculateDimensions(answers);
  const answeredCount = Object.keys(answers).length;
  const activeDimensions = dimensions.filter((dimension) => dimension.answered > 0);
  const strongest = [...activeDimensions].sort((a, b) => b.score - a.score)[0];
  const constraint = [...activeDimensions].sort((a, b) => a.score - b.score)[0];
  const interpretation = !strongest
    ? "Your answers will separate opportunity potential from the evidence and delivery constraints behind it."
    : strongest.dimension === constraint.dimension
      ? `${strongest.label} is the first signal. More answers will separate value from readiness.`
      : `${strongest.label} has the strongest execution evidence; ${constraint.label.toLowerCase()} is the current delivery constraint.`;
  return (
    <aside className={compact ? "strategy-signal compact" : "strategy-signal"} aria-label="Live strategy signal">
      <div className="signal-heading">
        <span>Opportunity signal</span>
        <strong>{answeredCount}/{questions.length}</strong>
      </div>
      {!compact && <div className="signal-list">
        {dimensions.map((dimension) => (
          <div className="signal-row" key={dimension.dimension}>
            <div className="signal-label"><span>{dimension.label}</span><strong>{dimension.answered ? dimension.score : "—"}</strong></div>
            <div className="signal-track" aria-hidden="true"><span style={{ transform: `scaleX(${dimension.answered ? Math.max(dimension.score, 5) / 100 : 0})` }} /></div>
          </div>
        ))}
      </div>}
      <p className={compact ? "signal-interpretation compact" : "signal-interpretation"} aria-live="polite">{interpretation}</p>
      {!compact && <p className="signal-note">The signal changes as answers combine. Your report will explain every priority it surfaces.</p>}
    </aside>
  );
}

function Progress({ current }: { current: number }) {
  const percentage = Math.round(((current + 1) / questions.length) * 100);
  return (
    <div className="progress" aria-label={`Assessment ${percentage}% complete`}>
      <div className="progress-meta"><span>{dimensionLabels[questions[current].dimension]}</span><strong>{percentage}%</strong></div>
      <div className="progress-track"><span style={{ width: `${percentage}%` }} /></div>
    </div>
  );
}

function ImportScreen({
  analysis,
  onAnalysis,
  onBack,
  onContinue,
  onSkip,
}: {
  analysis: CostImportAnalysis | null;
  onAnalysis: (analysis: CostImportAnalysis | null) => void;
  onBack: () => void;
  onContinue: () => void;
  onSkip: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [status, setStatus] = useState<"idle" | "reading" | "error">("idle");
  const [error, setError] = useState("");

  const readFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setStatus("error");
      setError("Choose a .csv file exported from AWS Cost Optimization Hub.");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setStatus("error");
      setError("This file is larger than 50 MB. Filter the export or split it into a smaller recommendation set.");
      return;
    }
    setStatus("reading");
    setError("");
    try {
      const parsed = analyzeCostOptimizationCsv(await file.text(), file.name);
      onAnalysis(parsed);
      setStatus("idle");
    } catch (reason) {
      onAnalysis(null);
      setStatus("error");
      setError(reason instanceof Error ? reason.message : "We could not read this CSV.");
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const [file] = Array.from(event.dataTransfer.files);
    if (file) void readFile(file);
  };

  const loadSample = () => {
    onAnalysis(analyzeCostOptimizationCsv(sampleCostOptimizationCsv, sampleCsvFileName));
    setStatus("idle");
    setError("");
  };

  return (
    <main className="import-shell" id="main-content">
      <section className="import-main">
        <button className="back-link" onClick={onBack}><ArrowLeft aria-hidden="true" /> Back</button>
        <h1 tabIndex={-1}>Bring the AWS evidence.</h1>
        <p className="lead-copy">Upload the recommendations CSV from AWS Cost Optimization Hub. We analyze it inside this browser, then ask only the questions AWS cannot answer.</p>

        <div
          className={`upload-zone ${dragging ? "dragging" : ""} ${analysis ? "complete" : ""}`}
          onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            onChange={(event) => { const file = event.target.files?.[0]; if (file) void readFile(file); event.target.value = ""; }}
          />
          {analysis ? <ShieldCheck aria-hidden="true" /> : <FileUp aria-hidden="true" />}
          <div>
            <h2>{analysis ? `${analysis.rowCount} recommendations recognized` : "Drop the AWS recommendations CSV here"}</h2>
            <p>{analysis ? `${analysis.fileName} · ${money(analysis.totalMonthlySavings)}/month estimated opportunity` : "Or choose the downloaded CSV from your computer. Maximum file size: 50 MB."}</p>
          </div>
          <button className={analysis ? "outline-button" : "primary-button"} onClick={() => inputRef.current?.click()} disabled={status === "reading"}>
            {status === "reading" ? <RefreshCw className="spin" aria-hidden="true" /> : <FileUp aria-hidden="true" />}
            {status === "reading" ? "Reading CSV…" : analysis ? "Replace CSV" : "Choose CSV"}
          </button>
        </div>

        {error && <div className="import-error" role="alert"><AlertTriangle aria-hidden="true" /><div><strong>The CSV could not be analyzed.</strong><p>{error}</p></div></div>}

        {analysis && (
          <>
            <section className="import-proof" aria-label="Imported recommendation summary">
              <div><strong>{money(analysis.totalMonthlySavings)}</strong><span>monthly AWS estimate</span></div>
              <div><strong>{money(analysis.annualizedSavings)}</strong><span>annualized opportunity</span></div>
              <div><strong>{analysis.accountCount}</strong><span>AWS accounts represented</span></div>
              <div><strong>{analysis.topTenSavingsShare}%</strong><span>of savings in the top ten</span></div>
            </section>
            {analysis.warnings.length > 0 && <div className="evidence-warning" role="status"><AlertTriangle aria-hidden="true" /><div><strong>Analysis limitation</strong><p>{analysis.warnings.join(" ")}</p></div></div>}
          </>
        )}

        <div className="sample-actions">
          <div><Database aria-hidden="true" /><span><strong>Want to see the expected format?</strong> Use our fictional Northstar dataset or download it for review.</span></div>
          <div><button className="text-button" onClick={loadSample}>Use sample data</button><button className="text-button" onClick={downloadSampleCsv}><Download aria-hidden="true" /> Download sample CSV</button></div>
        </div>

        <details className="export-guide">
          <summary>How to generate the CSV in AWS <ArrowRight aria-hidden="true" /></summary>
          <div className="guide-body">
            <div>
              <h2>Export Cost Optimization Hub recommendations</h2>
              <p>This is not a Cost and Usage Report. It is the recommendation table AWS already prepares.</p>
            </div>
            <ol>
              <li><strong>Open AWS Billing and Cost Management.</strong><span>Sign in to the account that can view the required organization or member-account recommendations.</span></li>
              <li><strong>Open Cost Optimization Hub.</strong><span>If AWS asks you to enable it, complete that setup and wait for recommendations to populate.</span></li>
              <li><strong>Choose Savings opportunities.</strong><span>Clear filters for a complete assessment, or intentionally filter to the scope you want analyzed.</span></li>
              <li><strong>Choose Download as CSV.</strong><span>A new browser tab starts the download. Keep it open until the file finishes.</span></li>
              <li><strong>Return here and upload the CSV.</strong><span>We validate the columns and show the recognized totals before asking any questions.</span></li>
            </ol>
            <a href="https://docs.aws.amazon.com/cost-management/latest/userguide/coh-export-recommendations.html" target="_blank" rel="noreferrer">Read the official AWS export instructions <ExternalLink aria-hidden="true" /></a>
          </div>
        </details>

        <div className="import-footer-actions">
          <button className="primary-button" onClick={onContinue} disabled={!analysis}>Continue with this evidence <ArrowRight aria-hidden="true" /></button>
          <button className="text-button" onClick={onSkip}>Continue without AWS data</button>
        </div>
        <p className="privacy-note"><LockKeyhole aria-hidden="true" /> The CSV is parsed locally and never uploaded. When you build the assessment, only aggregate findings and structured selections may be sent to an external AI service for narrative refinement. Free-text fields, raw rows, filenames, account IDs, resource IDs, company names, and respondent names are excluded.</p>
      </section>
      <aside className="import-aside">
        <div><span>AWS provides</span><strong>Cost, savings, action, effort, restart, rollback, account, Region, and resource evidence.</strong></div>
        <div><span>You provide</span><strong>Ownership, risk tolerance, change controls, engineering capacity, and realization practices.</strong></div>
        <p>The report combines both sources and labels each conclusion so an estimate never masquerades as realized value.</p>
      </aside>
    </main>
  );
}

function ResultView({ result, onBack, onRestart }: { result: AssessmentResult; onBack: () => void; onRestart: () => void }) {
  const [downloadState, setDownloadState] = useState<"idle" | "working" | "error">("idle");
  const download = async () => {
    setDownloadState("working");
    try {
      const { downloadStrategyReport } = await import("./pdf/download");
      await downloadStrategyReport(result);
      setDownloadState("idle");
    } catch (error) {
      console.error(error);
      setDownloadState("error");
    }
  };
  const primaryLane = result.opportunityLanes.find((lane) => lane.id === result.primaryOpportunity) ?? result.opportunityLanes[0];
  return (
    <main className="result-shell" id="main-content">
      <section className="result-lead">
        <button className="result-back" onClick={onBack}><ArrowLeft aria-hidden="true" /> Back to assessment</button>
        <div>
          <p className="result-context">{result.context.companyName} · AWS cost optimization assessment</p>
          <h1>{result.executiveHeadline}</h1>
        </div>
        <div className="primary-lane" aria-label={`Primary opportunity lane: ${primaryLane.label}`}>
          <span>Start here</span>
          <strong>{primaryLane.label}</strong>
          <small>{primaryLane.measured ? `${money(primaryLane.measured.monthlySavings)}/month · ${primaryLane.measured.recommendationCount} recommendations` : "Highest-priority direction from the assessment"}</small>
        </div>
      </section>

      {result.costAnalysis && (
        <section className="result-section evidence-section">
          <div className="section-title"><h2>What the AWS export says</h2><p>Measured recommendation evidence is separated from the organization’s answers and from realized-value claims.</p></div>
          <div className="evidence-metrics">
            <article><strong>{money(result.costAnalysis.totalMonthlySavings)}</strong><span>estimated monthly savings</span><small>{result.costAnalysis.savingsRate === null ? "Savings rate unavailable: monthly-cost evidence is incomplete" : `${result.costAnalysis.savingsRate}% of represented monthly cost`}</small></article>
            <article><strong>{money(result.costAnalysis.annualizedSavings)}</strong><span>annualized opportunity</span><small>Directional if all recommendations were implemented</small></article>
            <article><strong>{result.costAnalysis.rowCount}</strong><span>recommendations analyzed</span><small>{result.costAnalysis.accountCount} accounts · {result.costAnalysis.regionCount} Regions</small></article>
            <article><strong>{result.costAnalysis.topTenSavingsShare}%</strong><span>value in the top ten</span><small>{result.costAnalysis.lowValueTailCount} recommendations below $100/month</small></article>
          </div>
          {result.costAnalysis.warnings.length > 0 && <div className="evidence-warning result-warning" role="status"><AlertTriangle aria-hidden="true" /><div><strong>Evidence limitation</strong><p>{result.costAnalysis.warnings.join(" ")}</p></div></div>}
          <div className="action-breakdown" aria-label="Savings by recommended action">
            {result.costAnalysis.actionSummaries.slice(0, 6).map((action) => (
              <div key={action.key}>
                <div><strong>{action.label}</strong><span>{money(action.monthlySavings)} · {action.recommendationCount} items</span></div>
                <div className="action-track"><span style={{ width: `${action.shareOfSavings}%` }} /></div>
                <b>{action.shareOfSavings}%</b>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="result-section executive-summary-section">
        <div className="section-title"><h2>What {result.context.companyName || "your organization"} should do</h2><p>Three decisions generated by connecting the answers—not by assigning a generic maturity score.</p></div>
        <div className="executive-decision-list">
          {result.executiveDecisions.map((decision) => (
            <article className="executive-decision" key={decision.label}>
              <strong>{decision.label}</strong>
              <div><h3>{decision.action}</h3><p>{decision.reason}</p></div>
            </article>
          ))}
        </div>
      </section>

      <div className="result-detail-sections">
      <section className="result-section">
        <div className="section-title"><h2>Your opportunity portfolio</h2><p>Four AWS plays with different economics, effort, and decision paths.</p></div>
        <p className="portfolio-thesis">{result.portfolioThesis}</p>
        <div className="portfolio-facts">
          {result.portfolioFacts.map((fact) => <p key={fact}>{fact}</p>)}
        </div>
        <div className="opportunity-grid">
          {result.opportunityLanes.map((lane, index) => (
            <article key={lane.id} className="opportunity-card">
              <div className="opportunity-rank"><span>{String(index + 1).padStart(2, "0")}</span><strong>{lane.potential}</strong></div>
              <h3>{lane.label}</h3>
              <p className="opportunity-shape">{lane.valueShape}</p>
              <dl><div><dt>Readiness</dt><dd>{lane.readiness}</dd></div><div><dt>Delivery reality</dt><dd>{lane.effort}</dd></div></dl>
              <p>{lane.thesis}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="result-section decision-section">
        <div className="section-title"><h2>The decision behind each opportunity</h2><p>A recommendation is only a signal until its evidence gate is satisfied.</p></div>
        <div className="decision-stack">
          {result.opportunityLanes.map((lane, index) => (
            <article key={lane.id} className="decision-row">
              <span>{String(index + 1).padStart(2, "0")}</span>
              <div><h3>{lane.label}</h3><p><strong>Decision gate:</strong> {lane.decisionGate}</p><p><strong>First move:</strong> {lane.firstMove}</p></div>
              <ul>{lane.validation.map((item) => <li key={item}>{item}</li>)}</ul>
            </article>
          ))}
        </div>
      </section>

      {result.costAnalysis && (
        <section className="result-section evidence-table-section">
          <div className="section-title"><h2>The recommendations driving the result</h2><p>The highest-value imported findings are shown without exposing resource identifiers.</p></div>
          <div className="recommendation-table" role="table" aria-label="Highest-value AWS recommendations">
            <div className="recommendation-table-head" role="row">
              <span role="columnheader">Recommendation</span><span role="columnheader">Scope</span><span role="columnheader">Delivery</span><span role="columnheader">Monthly savings</span>
            </div>
            {result.costAnalysis.topRecommendations.map((item, index) => (
              <div className="recommendation-table-row" role="row" key={`${item.actionType}-${item.resourceType}-${index}`}>
                <div role="cell"><strong>{item.actionType.replace(/([a-z])([A-Z])/g, "$1 $2")}</strong><small>{item.resourceType.replace(/([a-z])([A-Z])/g, "$1 $2")}</small></div>
                <div role="cell"><strong>{item.region}</strong><small>Account ending {item.accountId.slice(-4)}</small></div>
                <div role="cell"><strong>{item.implementationEffort} effort</strong><small>{item.restartNeeded === true ? "Restart required" : item.restartNeeded === false ? "No restart" : "Restart unknown"} · {item.rollbackPossible === true ? "Rollback available" : item.rollbackPossible === false ? "Rollback not indicated" : "Rollback unknown"}</small></div>
                <div role="cell"><strong>{money(item.monthlySavings)}</strong><small>{item.savingsPercentage}% estimated reduction</small></div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="result-section pale-section">
        <div className="section-title"><h2>What the combined answers reveal</h2><p>Cross-answer signals expose dependencies a scorecard misses.</p></div>
        <div className="insight-stack">
          {result.insights.map((insight, index) => (
            <article key={insight.title} className={`insight-row ${insight.tone}`}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <div><h3>{insight.title}</h3><p>{insight.body}</p></div>
            </article>
          ))}
        </div>
      </section>

      <section className="result-section">
        <div className="section-title"><h2>Your prioritized execution plays</h2><p>Ranked by opportunity shape, readiness, risk, and dependency.</p></div>
        <div className="recommendation-stack">
          {result.recommendations.map((recommendation) => (
            <article key={recommendation.id} className="recommendation-row">
              <span>{String(recommendation.priority).padStart(2, "0")}</span>
              <div className="recommendation-copy"><h3>{recommendation.title}</h3><p>{recommendation.why}</p><small>{recommendation.horizon} · {recommendation.owner} · {recommendation.impact} impact</small></div>
            </article>
          ))}
        </div>
      </section>

      <section className="result-section roadmap-section">
        <div className="section-title"><h2>Your 90-day roadmap</h2><p>Ownership and evidence come before scale.</p></div>
        <div className="roadmap-grid">
          {result.roadmap.map((phase) => (
            <article key={phase.label}>
              <span>{phase.label}</span><h3>{phase.objective}</h3>
              <ul>{phase.actions.map((action) => <li key={action}>{action}</li>)}</ul>
              <p><strong>Evidence of progress:</strong> {phase.outcome}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="result-section support-section">
        <div className="section-title"><h2>Where Driven can accelerate the strategy</h2><p>Specialist support follows your priorities; it does not replace your ownership.</p></div>
        <div className="support-grid">
          {result.recommendations.slice(0, 4).map((recommendation) => (
            <article key={recommendation.id} className="support-card">
              <h3>{recommendation.title}</h3>
              <p>{recommendation.drivenSupport}</p>
              <small><strong>Why this mapped:</strong> {recommendation.evidence.join(" · ")}</small>
            </article>
          ))}
        </div>
      </section>

      <section className="result-section method-section">
        <div className="section-title"><h2>Use the assessment to structure the next conversation</h2><p>{result.costAnalysis ? "The CSV quantifies AWS estimates; the survey explains whether the organization can realize them." : "The survey provides direction—not a claim of measured savings."}</p></div>
        <div className="method-grid">
          <div className="method-copy">
            <h3>Method</h3>
            <p>The deterministic engine combines {result.costAnalysis ? `${result.costAnalysis.rowCount} imported Cost Optimization Hub recommendations` : "customer-provided context"} with {questions.length} structured questions across ownership, safe automation, change evidence, financial risk, migration readiness, and value realization. It calculates every number, classifies the opportunity lanes, and fixes the action and recommendation order. An AI editorial pass may sharpen the narrative; it is accepted only when every citation ID is known, numeric and qualitative wording clears the cited-fact checks, prohibited promises are absent, and every action still matches the deterministic action set.</p>
            <h3>Limitations</h3>
            <p>{result.costAnalysis ? `A Cost Optimization Hub CSV named “${result.costAnalysis.fileName}” was analyzed locally. Raw CSV rows, filenames, company and respondent names, account IDs, and resource IDs stay in the browser; only aggregate evidence facts and assessment selections may be sent for narrative synthesis. AWS estimates are normalized indicators, not forecasts of realized savings. No CUR, workload telemetry, contract inventory, or live resource access was analyzed.` : "No AWS Cost Optimization Hub export, CUR, workload telemetry, contract inventory, or resource identifiers were analyzed. Opportunity value is not independently quantified."} If the editorial service is unavailable or any claim fails validation, the deterministic narrative is used. The report does not prescribe destructive actions or recommend a specific Savings Plan or Reserved Instance purchase.</p>
            <h3>Recommended discussion prompts</h3>
            <ol>
              <li>Which opportunity lane carries material value but cannot pass its decision gate today?</li>
              <li>Which recommendations belong in human review, grouped engineering work, or safe automation?</li>
              <li>What proof would move one opportunity from AWS-estimated to realized value?</li>
            </ol>
          </div>
          <aside className="next-step-panel" aria-label="Suggested next step">
            <h3>Suggested next step</h3>
            <p>Run a focused working session with finance, cloud platform, architecture, and two representative workload owners.</p>
            <p>Segment the portfolio into financial decisions, reversible changes, migration programs, and low-value automation.</p>
            <p>Driven can validate the economics, build the execution patterns, and establish the opportunity-to-realization operating board.</p>
          </aside>
        </div>
      </section>
      </div>

      <section className="download-panel">
        <div><FileText aria-hidden="true" /><h2>Your AWS optimization assessment is ready.</h2><p>A Sanjeev-style, client-ready assessment with imported AWS evidence, value concentration, opportunity lanes, decision gates, and a 90-day execution plan. The complete assessment above is also available as accessible HTML.</p></div>
        <div className="download-actions">
          <button className="primary-button" onClick={download} disabled={downloadState === "working"}>
            {downloadState === "working" ? <RefreshCw className="spin" aria-hidden="true" /> : <Download aria-hidden="true" />}
            {downloadState === "working" ? "Building your PDF…" : "Download assessment PDF"}
          </button>
          <button className="text-button" onClick={onRestart}>Start a new assessment</button>
          {downloadState === "error" && <p className="error-message" role="alert">The PDF could not be generated. Please try again.</p>}
        </div>
      </section>
    </main>
  );
}

function AssessmentApp() {
  const [appState, setAppState] = useState<AppHistoryState>(initialAppState);
  const [generationState, setGenerationState] = useState<"idle" | "working">("idle");
  const { screen, context, answers, costAnalysis, questionIndex, result } = appState;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const pendingResetRef = useRef<AppHistoryState | null>(null);

  const navigate = (updates: Partial<AppHistoryState>) => {
    const next = { ...appState, ...updates, __finopsAssessment: true as const, depth: appState.depth + 1 };
    window.history.pushState(next, "", routeFor(next));
    setAppState(next);
  };

  const goBack = (fallback: Partial<AppHistoryState>) => {
    if (appState.depth > 0) window.history.back();
    else navigate(fallback);
  };

  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      if (pendingResetRef.current) {
        const next = pendingResetRef.current;
        pendingResetRef.current = null;
        // Pushing from the assessment's original entry discards every forward
        // entry that held the completed assessment's derived state.
        window.history.pushState(next, "", routeFor(next));
        setAppState(next);
        return;
      }
      if (isAppHistoryState(event.state)) setAppState(event.state);
      else setAppState(initialAppState());
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    window.history.replaceState(appState, "", routeFor(appState));
  }, [appState]);

  useEffect(() => {
    headingRef.current?.focus();
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [screen, questionIndex]);

  const currentQuestion = questions[questionIndex];
  const contextComplete = fieldOrder.every((field) => context[field].trim());
  const assessmentComplete = questions.every((question) => Boolean(answers[question.id]));
  const previewDimensions = useMemo(() => calculateDimensions(answers), [answers]);

  const complete = async () => {
    if (generationState === "working") return;
    setGenerationState("working");
    const deterministicResult = buildAssessmentResult(context, answers, costAnalysis);
    const assessmentResult = await enhanceAssessmentNarrative(deterministicResult, answers);
    setGenerationState("idle");
    navigate({ screen: "result", result: assessmentResult });
  };

  const restart = (screen: "intro" | "import" = "intro") => {
    const next = { ...baseState(), screen };
    if (appState.depth > 0) {
      pendingResetRef.current = next;
      window.history.go(-appState.depth);
      return;
    }
    window.history.replaceState(next, "", routeFor(next));
    setAppState(next);
  };

  if (screen === "result" && result) return <><Header /><ResultView result={result} onBack={() => goBack({ screen: "intro" })} onRestart={() => restart()} /><DrivenFooter /></>;

  return (
    <div className="app-frame">
      <Header />
      {screen === "intro" && (
        <main className="landing" id="main-content">
          <div className="intro">
            <section className="intro-copy">
              <div className="intro-inner">
                <h1 ref={headingRef} tabIndex={-1}>Turn AWS opportunity signals into a strategy you can act on.</h1>
                <p>Separate headline savings from delivery reality. Identify the financial decisions, migration programs, engineering changes, and automation plays that can actually produce value.</p>
                <div className="intro-actions">
                  <button className="primary-button" onClick={() => navigate({ screen: "import" })}>Begin assessment <ArrowRight aria-hidden="true" /></button>
                  <a className="secondary-button" href="/example/?viewer=pdfjs"><FileText aria-hidden="true" /> See an evidence-backed example</a>
                </div>
                <div className="privacy-line"><LockKeyhole aria-hidden="true" /><span>Your CSV stays in this browser. On generation, only minimized aggregate evidence and structured selections may be sent to an external AI service for narrative refinement; free-text and dedicated identity fields are excluded. No AWS account connection or CUR export is required.</span></div>
              </div>
            </section>
            <section className="intro-film" id="product-film" aria-labelledby="film-title">
              <div className="intro-film-heading">
                <h2 id="film-title">See how evidence becomes a strategy.</h2>
                <span>78-second product film</span>
              </div>
              <div className="film-player">
                <video controls preload="metadata" playsInline poster="/media/finops-lasius-product-film-poster.jpg" aria-label="How the Driven AWS Opportunity Strategy works">
                  <source src="/media/finops-lasius-product-film.mp4" type="video/mp4" />
                  <track kind="captions" src="/media/finops-lasius-product-film.vtt" srcLang="en" label="English" default />
                  Your browser does not support embedded video. <a href="/media/finops-lasius-product-film.mp4">Download the product film</a>.
                </video>
              </div>
              <p>AWS evidence and twelve business questions become deterministic analysis, controlled AI narrative, and a tailored client report.</p>
              <div className="film-proof" aria-label="What the film covers">
                <span>AWS evidence</span>
                <span>Business context</span>
                <span>Controlled AI</span>
              </div>
            </section>
          </div>
        </main>
      )}

      {screen === "import" && (
        <ImportScreen
          analysis={costAnalysis}
          onAnalysis={(nextAnalysis) => setAppState((current) => ({ ...current, costAnalysis: nextAnalysis }))}
          onBack={() => goBack({ screen: "intro" })}
          onContinue={() => navigate({ screen: "context" })}
          onSkip={() => navigate({ screen: "context", costAnalysis: null })}
        />
      )}

      {screen === "context" && (
        <main className="workflow-shell" id="main-content">
          <section className="workflow-main">
            <button className="back-link" onClick={() => goBack({ screen: "import" })}><ArrowLeft aria-hidden="true" /> Back to AWS evidence</button>
            <h1 ref={headingRef} tabIndex={-1}>Tell us what AWS cannot.</h1>
            <p className="lead-copy">The export provides the opportunity evidence. This short profile sets the business objective, decision horizon, and accountable audience for the assessment.</p>
            <form className="context-form" onSubmit={(event) => { event.preventDefault(); if (contextComplete) navigate({ screen: "questions", questionIndex: 0 }); }}>
              {fieldOrder.map((field) => (
                <label key={field}>
                  <span>{contextLabels[field]}</span>
                  {selectOptions[field] ? (
                    <select value={context[field]} onChange={(event) => setAppState((current) => ({ ...current, context: { ...current.context, [field]: event.target.value } }))} required>
                      <option value="">Select one</option>
                      {selectOptions[field]!.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  ) : (
                    <input value={context[field]} onChange={(event) => setAppState((current) => ({ ...current, context: { ...current.context, [field]: event.target.value } }))} required autoComplete={field === "companyName" ? "organization" : field === "respondentName" ? "name" : "organization-title"} />
                  )}
                </label>
              ))}
              <button className="primary-button form-submit" disabled={!contextComplete}>Continue to assessment <ArrowRight aria-hidden="true" /></button>
            </form>
          </section>
          <StrategySignal answers={answers} />
        </main>
      )}

      {screen === "questions" && (
        <main className="workflow-shell question-workflow" id="main-content">
          <section className="workflow-main question-main">
            <button className="back-link" onClick={() => goBack({ screen: questionIndex === 0 ? "context" : "questions", questionIndex: Math.max(0, questionIndex - 1) })}><ArrowLeft aria-hidden="true" /> Back</button>
            <Progress current={questionIndex} />
            <h1 ref={headingRef} tabIndex={-1}>{currentQuestion.prompt}</h1>
            <p className="lead-copy">{currentQuestion.help}</p>
            <fieldset className="option-list">
              <legend className="sr-only">Choose the response that best reflects your organization</legend>
              {currentQuestion.options.map((option) => {
                const selected = answers[currentQuestion.id] === option.value;
                return (
                  <label key={option.value} className={selected ? "option selected" : "option"}>
                    <input type="radio" name={currentQuestion.id} value={option.value} checked={selected} onChange={() => setAppState((current) => ({ ...current, answers: { ...current.answers, [currentQuestion.id]: option.value } }))} />
                    <span className="option-mark">{selected && <Check aria-hidden="true" />}</span>
                    <span><strong>{option.label}</strong><small>{option.description}</small></span>
                  </label>
                );
              })}
            </fieldset>
            <div className="mobile-signal"><StrategySignal answers={answers} compact /></div>
            <div className="question-actions">
              <span>Question {questionIndex + 1} of {questions.length}</span>
              <button className="primary-button" disabled={!answers[currentQuestion.id]} onClick={() => questionIndex === questions.length - 1 ? navigate({ screen: "review" }) : navigate({ screen: "questions", questionIndex: questionIndex + 1 })}>{questionIndex === questions.length - 1 ? "Review assessment" : "Next question"} <ArrowRight aria-hidden="true" /></button>
            </div>
          </section>
          <StrategySignal answers={answers} />
        </main>
      )}

      {screen === "review" && (
        <main className="workflow-shell" id="main-content">
          <section className="workflow-main review-main">
            <button className="back-link" onClick={() => goBack({ screen: "questions", questionIndex: questions.length - 1 })}><ArrowLeft aria-hidden="true" /> Back to answers</button>
            <h1 ref={headingRef} tabIndex={-1}>Review the evidence before building the opportunity portfolio.</h1>
            <p className="lead-copy">All six decision lenses are complete. They describe the organization’s ability to act on the imported AWS evidence—not a generic maturity grade.</p>
            <div className="review-profile">
              {previewDimensions.map((dimension) => (
                <button key={dimension.dimension} onClick={() => { const index = questions.findIndex((question) => question.dimension === dimension.dimension); navigate({ screen: "questions", questionIndex: index }); }}>
                  <span><strong>{dimension.label}</strong><small>{dimension.answered}/{dimension.total} answered</small></span>
                  <b>{dimension.score}</b>
                </button>
              ))}
            </div>
            <div className="review-summary"><span>Prepared for</span><strong>{context.companyName}</strong><p>{context.industry} · {costAnalysis ? `${costAnalysis.rowCount} AWS recommendations across ${costAnalysis.accountCount} accounts` : "Survey-only assessment"} · {selectOptions.primaryGoal?.find(([value]) => value === context.primaryGoal)?.[1]}</p></div>
            <button className="primary-button generate-button" onClick={() => void complete()} disabled={!assessmentComplete || generationState === "working"} aria-live="polite">
              {generationState === "working" ? <RefreshCw className="spin" aria-hidden="true" /> : <Sparkles aria-hidden="true" />}
              {generationState === "working" ? "Connecting the strongest evidence…" : "Build my AWS optimization assessment"}
            </button>
          </section>
          <StrategySignal answers={answers} />
        </main>
      )}
      <DrivenFooter />
    </div>
  );
}

export default function App() {
  if (window.location.pathname === "/example" || window.location.pathname === "/example/") {
    return (
      <Suspense fallback={<main className="pdf-viewer-state"><RefreshCw className="spin" aria-hidden="true" /><p>Opening the evidence-backed example…</p></main>}>
        <PdfExampleViewer />
      </Suspense>
    );
  }

  return <AssessmentApp />;
}

function Header() {
  return <header className="site-header"><a href="#main-content" className="skip-link">Skip to content</a><div className="brand"><strong>DRIVEN</strong><span>AWS Cost Optimization Assessment</span></div><div className="header-meta">Private by design</div></header>;
}

function DrivenFooter() {
  return <footer className="driven-footer"><strong>DRIVEN</strong><span>Strategy · Architecture · Execution</span></footer>;
}
